import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import {
  EsraPipingAPIClient,
  FileParameter,
  FileResponse
} from 'src/app/shared/models/autogenerated-piping';
import { indicate } from 'src/app/operators';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { UserService } from 'src/app/core/services/user.service';
import { Constants } from 'src/app/shared/constants/constants';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';

export type BulkAction = 'verify' | 'upload';

@Component({
  selector: 'app-valve-bulk-upload',
  templateUrl: './valve-bulk-upload.component.html',
  styleUrls: ['./valve-bulk-upload.component.scss'],
  standalone: false
})
export class ValveBulkUploadComponent implements OnInit, OnDestroy {

  // ── State ──────────────────────────────────────────────────────────────────
  isDownloading$ = new BehaviorSubject<boolean>(false);
  isProcessing$ = new BehaviorSubject<boolean>(false);

  selectedFile: File | null = null;
  selectedAction: BulkAction | null = null;
  hasWritePermission = false;

  private readonly subscription = new Subscription();

  constructor(
    private esraPipingApiClient: EsraPipingAPIClient,
    private snackBarService: SnackBarService,
    private userService: UserService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.checkPermissions();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ── Permission Check ───────────────────────────────────────────────────────
  private checkPermissions(): void {
    this.subscription.add(
      this.userService.isRoleExistsObservable(Constants.valvesBulkUploadWriter)
        .pipe(first())
        .subscribe({
          next: (hasRole) => {
            this.hasWritePermission = hasRole;
          }
        })
    );
  }

  // ── File Selection ─────────────────────────────────────────────────────────
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      // Validate file type
      const fileName = file.name.toLowerCase();
      const isValidExtension = fileName.endsWith('.xlsx');

      if (!isValidExtension) {
        this.snackBarService.showError('Only Excel workbook files (.xlsx) are supported.');
        this.selectedFile = null;
        input.value = ''; // Reset the input
        return;
      }

      this.selectedFile = file;
    } else {
      // This can occur when the file is locked by another process or was modified after selection
      this.snackBarService.showError('Could not read the selected file. It may be in use or unavailable. Please try again.');
      this.selectedFile = null;
      input.value = ''; // Reset the input so the user can re-select
    }
  }

  clearFile(): void {
    this.selectedFile = null;
  }

  // ── Action Selection ───────────────────────────────────────────────────────
  onActionChange(action: BulkAction): void {
    this.selectedAction = action;
  }

  // ── Run Button State ───────────────────────────────────────────────────────
  get isRunDisabled(): boolean {
    // Disable if no action selected, no file selected, or processing
    if (!this.selectedAction || !this.selectedFile || this.isProcessing$.getValue()) {
      return true;
    }
    // Disable Upload action if user doesn't have write permission
    if (this.selectedAction === 'upload' && !this.hasWritePermission) {
      return true;
    }
    return false;
  }

  // ── Download All ───────────────────────────────────────────────────────────
  onDownloadAll(): void {
    this.subscription.add(
      this.esraPipingApiClient.downloadAll()
        .pipe(
          indicate(this.isDownloading$),
          tap((response: FileResponse) => {
            this.downloadFile(response, 'ValveData.zip');
            this.snackBarService.showInfo('Download completed successfully.');
          })
        )
        .subscribe({
          error: (err) => {
            console.error('Download failed:', err);
            this.snackBarService.showError('Failed to download valve data. Please try again.');
          }
        })
    );
  }

  // ── Run Action ─────────────────────────────────────────────────────────────
  onRun(): void {
    if (this.isRunDisabled) {
      return;
    }

    const fileParameter: FileParameter = {
      data: this.selectedFile,
      fileName: this.selectedFile!.name
    };

    if (this.selectedAction === 'verify') {
      this.runVerify(fileParameter);
    } else if (this.selectedAction === 'upload') {
      this.confirmAndRunUpload(fileParameter);
    }
  }

  private confirmAndRunUpload(fileParameter: FileParameter): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        dialogMessage: 'Are you sure you want to update valves? This action will apply changes from the uploaded file to the database.',
        confirmText: 'update',
        cancelText: 'cancel'
      }
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe({
        next: (confirmed) => {
          if (confirmed) {
            this.runUpload(fileParameter);
          }
        }
      })
    );
  }

  private runVerify(fileParameter: FileParameter): void {
    this.subscription.add(
      this.esraPipingApiClient.bulkVerify(fileParameter)
        .pipe(
          indicate(this.isProcessing$),
          tap((response: FileResponse) => {
            this.downloadFile(response, 'VerificationResult.xlsx');
            this.snackBarService.showInfo('Verification completed successfully. Results downloaded.');
          })
        )
        .subscribe({
          error: (err) => {
            console.error('Verification failed:', err);
            this.snackBarService.showError('Verification failed. Please check your file and try again. If the file is open in another application, close it and retry.');
          }
        })
    );
  }

  private runUpload(fileParameter: FileParameter): void {
    this.subscription.add(
      this.esraPipingApiClient.bulkUpload(fileParameter)
        .pipe(
          indicate(this.isProcessing$),
          tap((response: FileResponse) => {
            this.downloadFile(response, 'UploadResult.xlsx');
            this.snackBarService.showInfo('Upload completed successfully. Results downloaded.');
          })
        )
        .subscribe({
          error: (err) => {
            console.error('Upload failed:', err);
            this.snackBarService.showError('Upload failed. Please check your file and try again.');
          }
        })
    );
  }

  // ── File Download Helper ───────────────────────────────────────────────────
  private downloadFile(response: FileResponse, defaultFileName: string): void {
    const file = response.data;
    const fileUrl = URL.createObjectURL(file);
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = response.fileName || defaultFileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(fileUrl);
  }
}
